import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { precisionRound, toMoneyWithDecimals } from "../../../domain/google-tag-manager/shared-utils";
import { ProductVariantAvailabilityStatus } from "@swo/shared";
import { pipe } from "fp-ts/lib/function";
import { richTextToString } from "./string-renderer";
import { PDPSwarovskiProductFrontend } from "../PDP_SingleImageCarousel/state/types";
import type {
  EnrichedSwarovskiBundleProductFrontend,
  EnrichedSwarovskiProductFrontendBase,
  EnrichedSwarovskiSingleProductFrontend,
} from "@swo/storefront";
import { getJsonLdAvailability } from "./getJsonLdAvailability";
import { computeReviewsSlice } from "./computeReviewsSlice";

function getGeneralSection(product: EnrichedSwarovskiProductFrontendBase) {
  const image = product.images.thumbnail?.url;

  return {
    "@context": "http://schema.org/",
    "@type": "Product",
    name: product.name,
    description: product.longDescription ? richTextToString(product.longDescription) : "",
    image: image,
    brand: {
      "@type": "Brand",
      name: "Swarovski Optik",
    },
  };
}

const year = new Date().getFullYear();

export const PDPProductJsonLd: ReactTFC<{
  productAvailabilityPerVariant: (variantSku: string) => ProductVariantAvailabilityStatus;
  product: PDPSwarovskiProductFrontend;
}> = ({ product, productAvailabilityPerVariant }) => {
  const obj = {
    ...getGeneralSection(product),
    offers: product.variants.map(variant => {
      const jsonLdAvailability = pipe(variant.sku, productAvailabilityPerVariant, getJsonLdAvailability);

      const price = pipe(variant.price, toMoneyWithDecimals, (n: number) =>
        precisionRound(n, variant.price.fractionDigits),
      );

      return {
        "@type": "Offer",
        url: variant.path,
        sku: variant.sku,
        priceCurrency: variant.price.currencyCode,
        price: price,
        itemCondition: "http://schema.org/NewCondition",
        availability: jsonLdAvailability,
        priceValidUntil: `${year}-12-31`,
        seller: {
          "@type": "Organization",
          name: "Swarovski Optik",
        },
        gtin13: variant?.ean || "",
      };
    }),
    ...computeReviewsSlice(product.reviews),
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />;
};

export const BDPProductJsonLd: ReactTFC<{
  product: EnrichedSwarovskiBundleProductFrontend;
}> = ({ product }) => {
  const obj = {
    ...getGeneralSection(product),
    offers: [],
    ...computeReviewsSlice(null),
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />;
};
